import { INotification } from '@/bundles/Notification/interfaces/INotification';
import { IGetMyNotificationsParams } from '@/bundles/Notification/interfaces/IGetMyNotificationsParams';
import { IPaginationResponse } from '@/bundles/App/interfaces/IPaginationResponse';
import HttpTransport from '@/bundles/Http/transport/HttpTransport';
import ErrorMapper from '@/bundles/Http/mappers/ErrorMapper';
import NotificationMapper from '@/bundles/Notification/mappers/NotificationMapper';
import { Notification } from '@/bundles/Notification/models/Notification';

export default class NotificationService {
  private readonly transport: HttpTransport;
  private readonly errorMapper: ErrorMapper;
  private readonly recordMapper: NotificationMapper;

  constructor (transport: HttpTransport, errorMapper: ErrorMapper, recordMapper: NotificationMapper) {
    this.transport = transport;
    this.errorMapper = errorMapper;
    this.recordMapper = recordMapper;
  }

  async getMyNotifications ({
    take,
    skip,
    controller = null,
  }: IGetMyNotificationsParams): Promise<IPaginationResponse<Notification>> {
    try {
      // the bell lives in the top bar, which stays mounted across pages: a route change must not
      // abort its list, or the count drops to 0 until the next socket refresh
      const response = await this.transport.get(
        `/messaging/my-notifications`,
        { take, skip },
        controller,
        { options: { persistOnRouteChange: true } },
      );

      return {
        data: response.data.map((notification: INotification) => this.recordMapper.mapToModel(notification)),
        total: response.total,
      };
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async updateNotificationStatus (notificationId: number): Promise<Notification> {
    try {
      const notification = await this.transport.patch(`/messaging/${notificationId}/status-notification`, {});

      return this.recordMapper.mapToModel(notification);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async clearAllNotifications (): Promise<void> {
    try {
      return await this.transport.post(`/messaging/clear-notification`, {});
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }
}
